import type { AdditiveFunction } from '../dto/ingredient/additive-function.enum';

/** Lingue in cui l'elenco ingredienti si compone: solo quelle con i nomi dell'Allegato VII Parte C verificati. */
export const INGREDIENT_LIST_LOCALES = ['it', 'en'] as const;
export type IngredientListLocale = (typeof INGREDIENT_LIST_LOCALES)[number];

/**
 * Allegato VII Parte C Reg. UE 1169/2011, testo consolidato (CELEX
 * 02011R1169): le 24 categorie funzionali, nella forma singolare usata
 * davanti a un singolo additivo in etichetta ("conservante: E250", non
 * "conservanti: E250"). Una tabella per lingua: stessa regola per le
 * superfici backend e per l'anteprima del frontend.
 *
 * Due note in calce alla tabella derogano alla regola generale
 * "categoria: nome o numero E" — verificate contro il testo consolidato
 * (mirror ufficiale in lingua inglese, `legislation.gov.uk/eur/2011/1169/annex/VII`,
 * che riporta gli stessi ancoraggi di nota della Gazzetta Ufficiale UE):
 *
 * - Nota (2), su `MODIFIED_STARCH`: "The specific name or E number shall not
 *   be required to be indicated" — l'unica eccezione al "nome oppure numero
 *   E", gestita in `composeAdditiveDesignation`.
 * - Nota (1), su `EMULSIFYING_SALT`: "Only for processed cheeses and
 *   products based on processed cheeses" ("Soltanto per i formaggi fusi e i
 *   prodotti a base di formaggio fuso"). A differenza della nota (2), questa
 *   non è una deroga sulla FORMA della dichiarazione ma un vincolo su QUANDO
 *   la categoria è utilizzabile — e resta NON implementata: lo schema dati
 *   non porta, né su `Ingredient` né su `Recipe`/`CounterLabelData`, un
 *   segnale "questo è un formaggio fuso o un prodotto a base di formaggio
 *   fuso". Inventare un'euristica sul nome della ricetta/ingrediente sarebbe
 *   un falso positivo/negativo silenzioso su un vincolo di legge — peggio di
 *   non applicarlo. Il gap va colmato con un campo dedicato (proposta:
 *   `Ingredient.isProcessedCheeseProduct Boolean?`, stesso pattern tri-stato
 *   di `Ingredient.isCompound`).
 */
export const ADDITIVE_FUNCTION_LABELS: Readonly<
  Record<IngredientListLocale, Readonly<Record<AdditiveFunction, string>>>
> = {
  it: {
    ACID: 'Acidificante',
    ACIDITY_REGULATOR: 'Correttore di acidità',
    ANTICAKING_AGENT: 'Antiagglomerante',
    ANTIFOAMING_AGENT: 'Agente antischiumogeno',
    ANTIOXIDANT: 'Antiossidante',
    BULKING_AGENT: 'Agente di carica',
    COLOUR: 'Colorante',
    EMULSIFIER: 'Emulsionante',
    EMULSIFYING_SALT: 'Sale di fusione',
    FIRMING_AGENT: 'Agente di resistenza',
    FLAVOUR_ENHANCER: 'Esaltatore di sapidità',
    FLOUR_TREATMENT_AGENT: 'Agente di trattamento della farina',
    FOAMING_AGENT: 'Agente schiumogeno',
    GELLING_AGENT: 'Gelificante',
    GLAZING_AGENT: 'Agente di rivestimento',
    HUMECTANT: 'Umidificante',
    MODIFIED_STARCH: 'Amido modificato',
    PRESERVATIVE: 'Conservante',
    PROPELLANT_GAS: 'Gas propulsore',
    RAISING_AGENT: 'Agente lievitante',
    SEQUESTRANT: 'Sequestrante',
    STABILISER: 'Stabilizzante',
    SWEETENER: 'Edulcorante',
    THICKENER: 'Addensante',
  },
  en: {
    ACID: 'Acid',
    ACIDITY_REGULATOR: 'Acidity regulator',
    ANTICAKING_AGENT: 'Anti-caking agent',
    ANTIFOAMING_AGENT: 'Anti-foaming agent',
    ANTIOXIDANT: 'Antioxidant',
    BULKING_AGENT: 'Bulking agent',
    COLOUR: 'Colour',
    EMULSIFIER: 'Emulsifier',
    EMULSIFYING_SALT: 'Emulsifying salt',
    FIRMING_AGENT: 'Firming agent',
    FLAVOUR_ENHANCER: 'Flavour enhancer',
    FLOUR_TREATMENT_AGENT: 'Flour treatment agent',
    FOAMING_AGENT: 'Foaming agent',
    GELLING_AGENT: 'Gelling agent',
    GLAZING_AGENT: 'Glazing agent',
    HUMECTANT: 'Humectant',
    MODIFIED_STARCH: 'Modified starch',
    PRESERVATIVE: 'Preservative',
    PROPELLANT_GAS: 'Propellant gas',
    RAISING_AGENT: 'Raising agent',
    SEQUESTRANT: 'Sequestrant',
    STABILISER: 'Stabiliser',
    SWEETENER: 'Sweetener',
    THICKENER: 'Thickener',
  },
};

/**
 * Forma plurale delle stesse categorie ("emulsionanti: lecitina di soia,
 * E476"), come compare sulle etichette che raggruppano piu' additivi della
 * stessa funzione. Serve solo a RICONOSCERE la categoria in un elenco
 * incollato o letto (`label-ingredient-list-parser.ts`): la stampa usa sempre
 * la forma singolare di `ADDITIVE_FUNCTION_LABELS`.
 */
export const ADDITIVE_FUNCTION_PLURAL_LABELS: Readonly<
  Record<IngredientListLocale, Readonly<Record<AdditiveFunction, string>>>
> = {
  it: {
    ACID: 'Acidificanti',
    ACIDITY_REGULATOR: 'Correttori di acidità',
    ANTICAKING_AGENT: 'Antiagglomeranti',
    ANTIFOAMING_AGENT: 'Agenti antischiumogeni',
    ANTIOXIDANT: 'Antiossidanti',
    BULKING_AGENT: 'Agenti di carica',
    COLOUR: 'Coloranti',
    EMULSIFIER: 'Emulsionanti',
    EMULSIFYING_SALT: 'Sali di fusione',
    FIRMING_AGENT: 'Agenti di resistenza',
    FLAVOUR_ENHANCER: 'Esaltatori di sapidità',
    FLOUR_TREATMENT_AGENT: 'Agenti di trattamento della farina',
    FOAMING_AGENT: 'Agenti schiumogeni',
    GELLING_AGENT: 'Gelificanti',
    GLAZING_AGENT: 'Agenti di rivestimento',
    HUMECTANT: 'Umidificanti',
    MODIFIED_STARCH: 'Amidi modificati',
    PRESERVATIVE: 'Conservanti',
    PROPELLANT_GAS: 'Gas propulsori',
    RAISING_AGENT: 'Agenti lievitanti',
    SEQUESTRANT: 'Sequestranti',
    STABILISER: 'Stabilizzanti',
    SWEETENER: 'Edulcoranti',
    THICKENER: 'Addensanti',
  },
  en: {
    ACID: 'Acids',
    ACIDITY_REGULATOR: 'Acidity regulators',
    ANTICAKING_AGENT: 'Anti-caking agents',
    ANTIFOAMING_AGENT: 'Anti-foaming agents',
    ANTIOXIDANT: 'Antioxidants',
    BULKING_AGENT: 'Bulking agents',
    COLOUR: 'Colours',
    EMULSIFIER: 'Emulsifiers',
    EMULSIFYING_SALT: 'Emulsifying salts',
    FIRMING_AGENT: 'Firming agents',
    FLAVOUR_ENHANCER: 'Flavour enhancers',
    FLOUR_TREATMENT_AGENT: 'Flour treatment agents',
    FOAMING_AGENT: 'Foaming agents',
    GELLING_AGENT: 'Gelling agents',
    GLAZING_AGENT: 'Glazing agents',
    HUMECTANT: 'Humectants',
    MODIFIED_STARCH: 'Modified starches',
    PRESERVATIVE: 'Preservatives',
    PROPELLANT_GAS: 'Propellant gases',
    RAISING_AGENT: 'Raising agents',
    SEQUESTRANT: 'Sequestrants',
    STABILISER: 'Stabilisers',
    SWEETENER: 'Sweeteners',
    THICKENER: 'Thickeners',
  },
};

/**
 * Lingua delle tabelle per una lingua UI qualsiasi: le lingue senza tabella
 * verificata (ar, bn, es, ro, uk, zh) ripiegano su italiano e inglese, le
 * lingue in cui si leggono le etichette dei fornitori italiani.
 */
export function resolveIngredientListLocales(locale: string): readonly IngredientListLocale[] {
  const language = locale.toLowerCase().split(/[-_]/)[0];
  const primary = INGREDIENT_LIST_LOCALES.find((candidate) => candidate === language);
  return primary
    ? [primary, ...INGREDIENT_LIST_LOCALES.filter((other) => other !== primary)]
    : INGREDIENT_LIST_LOCALES;
}
